/**
 * mpPhotoService — server-side MP portrait photo cache
 *
 * How it works:
 *   1. The Python scraper fetches all current Commons MPs from Parliament Members API
 *      and stores name → photo_url in the mp_photos database table.
 *   2. The scraper exposes GET /mp-photos which returns the full map as JSON.
 *   3. This service fetches that endpoint ONCE per browser session (module-level cache).
 *   4. Components call getMpPhotoUrl(mpName) — returns the URL instantly if already loaded,
 *      or waits for the single in-flight fetch to complete.
 *
 * Cache lifetime:
 *   - In-memory (module-level): entire browser session — no repeated network calls.
 *   - Database: refreshed monthly by the scraper scheduler.
 *
 * Name cleaning:
 *   MP names from petition data often have "Rt Hon" prefix or " MP" suffix.
 *   Both are stripped before lookup to match Parliament API's nameDisplayAs format.
 */

const API_BASE = process.env.REACT_APP_API_BASE || (
  process.env.NODE_ENV === 'production'
    ? 'https://api.ukpetitiontracker.com/api'
    : 'http://localhost:3002/api'
);

// Module-level cache — populated once, reused for the entire session
let photoMap = null;
let fetchPromise = null;

/**
 * Returns the full MP name → photo URL map.
 * Only makes one HTTP request per session regardless of how many components call it.
 */
export const getMpPhotos = () => {
  if (photoMap) return Promise.resolve(photoMap);
  if (fetchPromise) return fetchPromise;

  fetchPromise = fetch(`${API_BASE}/mp-photos`)
    .then(r => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    })
    .then(data => {
      photoMap = data;
      fetchPromise = null;
      return data;
    })
    .catch(() => {
      fetchPromise = null;
      photoMap = {}; // empty map — photos just won't show, no crash
      return {};
    });

  return fetchPromise;
};

/**
 * Look up the portrait photo URL for a given MP name.
 * Handles "Rt Hon" prefix and " MP" suffix automatically.
 *
 * @param {string} mpName - Raw MP name, e.g. "Rt Hon Keir Starmer MP"
 * @returns {Promise<string|null>} Photo URL or null if not found
 */
/**
 * Reduce an MP name to the bare "Firstname Lastname" the photo map is keyed by.
 *
 * The two Parliament APIs title the same person differently: the petitions API
 * returns "Rt Hon Mark Francois MP" while the Members API nameDisplayAs is
 * "Mr Mark Francois". Stripping only "Rt Hon"/" MP" left every MP with an
 * honorific (98 of 650) unmatched and therefore without a photo.
 */
export const normaliseMpName = (name) => (name || '')
  .replace(/ MP$/i, '')
  .replace(/\s+(KC|QC|CBE|OBE|MBE|DBE|PC|JP)\b/gi, '')
  .replace(/^(Rt Hon|Right Hon)\s+/i, '')
  .replace(/^(Mr|Mrs|Ms|Miss|Mx|Dr|Sir|Dame|Lord|Lady|Baroness|Professor|Prof)\.?\s+/i, '')
  .replace(/\s+/g, ' ')
  .trim();

export const getMpPhotoUrl = async (mpName) => {
  if (!mpName) return null;
  const cleanName = normaliseMpName(mpName);
  const map = await getMpPhotos();
  return map[cleanName] || null;
};
